import { Button, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Fundraising } from "../../../../../api/types/fundraisingTypes";

interface Props {
  onClose: () => void;
  onCreated: (fundraising: Fundraising) => void;
  onUpdated: (fundraising: Fundraising) => void;
  fundraisingId?: string;
  fundraisingEtag?: string;
  fundraisingName?: string;
}

const createFundraising = withErrorHandling(adminApi.createFundraising);
const updateFundraising = withErrorHandling(adminApi.updateFundraising);

const EditFundraisingDialog = ({
  onClose,
  onCreated,
  onUpdated,
  fundraisingId,
  fundraisingEtag,
  fundraisingName,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(fundraisingName || "");
  const [isTouched, setIsTouched] = useState(false);

  const isValid = Boolean(name.trim());
  const isSaveDisabled = !isValid || (!!fundraisingName && name.trim() === fundraisingName.trim());

  const handleCreateFundraising = async () => {
    if (!isValid) {
      return;
    }

    setSaving(true);
    const [resp, error] = await createFundraising({ name: name.trim() });
    setSaving(false);

    if (error) {
      logError(error, "[EditFundraisingDialog]");
      sendNotificationError(error.message);
    } else {
      sendNotification(`Fundraising "${name}" created successfully`);
      onCreated(resp);
    }
  };

  const handleUpdateFundraising = async (id: string) => {
    if (!isValid) {
      return;
    }

    if (!fundraisingEtag) {
      sendNotificationError("Unable to rename: the fundraising version is missing. Please refresh and try again.");
      return;
    }

    setSaving(true);
    const [fundraising, error] = await updateFundraising(id, { expectedEtag: fundraisingEtag, name: name.trim() });
    setSaving(false);

    if (error) {
      sendNotificationError(error.message);
    } else {
      sendNotification(`Fundraising "${name}" updated successfully`);
      onUpdated(fundraising);
    }
  };

  const handleSubmit = async () => {
    if (fundraisingId === undefined) {
      await handleCreateFundraising();
    } else {
      await handleUpdateFundraising(fundraisingId);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setIsTouched(true);
  };

  const isNew = !fundraisingId;
  const showValidationError = !isValid && isTouched;

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{isNew ? "New Fundraising" : "Rename Fundraising"}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <TextField
          fullWidth
          autoFocus
          label="Name"
          value={name}
          onChange={handleNameChange}
          error={showValidationError}
          helperText={showValidationError ? "Name cannot be empty" : ""}
        />
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button disabled={isSaveDisabled} variant="contained" loading={saving} onClick={handleSubmit}>
          {isNew ? "Create" : "Save"}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditFundraisingDialog;
